import styled from '@emotion/styled';
import MuiDialog from '@mui/material/Dialog';

const PAGE_BG = '#F7F8FA';

export const RevokeBureauConsentWrapper = styled('div')(({ theme }: any) => ({
  backgroundColor: PAGE_BG,
  minHeight: '60vh',
  marginTop: '2.75rem',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginTop: '5.5625rem',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(4)
  },

  '.rbc-inner': {
    maxWidth: 720,
    width: '100%'
  },

  '.rbc-page-title': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em',
    marginBottom: theme.spacing(1)
  },

  '.rbc-subtitle': {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    maxWidth: 520
  },

  '.rbc-action-card': {
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    width: 'fit-content',
    maxWidth: '100%',
    cursor: 'pointer',
    outline: 'none',
    transition: 'background-color 0.15s ease',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5)
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)'
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${theme.palette.grey[400]}`
    }
  },

  '.rbc-action-card-text': {
    fontWeight: 400,
    fontSize: '1rem',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    pointerEvents: 'none'
  }
}));

export const RevokeBureauConsentDialog = styled(MuiDialog)(
  ({ theme }: any) => ({
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.45)'
    },
    '& .MuiPaper-root': {
      borderRadius: Number(theme.shape.borderRadius) * 2,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
    },

    '.rbc-dialog-content': {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
      }
    },

    '.rbc-dialog-title': {
      fontWeight: 700,
      marginBottom: theme.spacing(1)
    },

    '.rbc-dialog-description': {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3)
    },

    '.rbc-alert': {
      marginBottom: theme.spacing(2)
    },

    '.rbc-stack': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5)
    },

    '.rbc-field-label': {
      fontWeight: 700,
      display: 'block'
    },

    '.rbc-field-label--name': {
      marginBottom: theme.spacing(0.75)
    },

    '.rbc-field-label--phone': {
      marginBottom: theme.spacing(0.5)
    },

    '.rbc-field-hint': {
      color: theme.palette.text.secondary,
      display: 'block',
      marginBottom: theme.spacing(0.75)
    },

    '.rbc-consent-row': {
      alignItems: 'flex-start',
      marginLeft: 0,
      '& .MuiCheckbox-root': {
        paddingTop: theme.spacing(0.25)
      },
      '& .MuiFormControlLabel-label': {
        fontSize: '0.9375rem'
      }
    },

    '.rbc-dialog-actions': {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5),
      paddingTop: theme.spacing(1),
      gap: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
      }
    },

    '.rbc-btn-cancel': {
      borderColor: '#E0E0E0',
      color: theme.palette.text.primary,
      textTransform: 'none',
      borderRadius: 12,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      outline: 'none',
      '&:focus': { outline: 'none' },
      '&.Mui-focusVisible': {
        outline: 'none',
        borderColor: '#E0E0E0',
        boxShadow: `0 0 0 2px ${theme.palette.grey[400]}`
      }
    },

    '.rbc-btn-submit': {
      textTransform: 'none',
      borderRadius: 12,
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: 'none',
      outline: 'none',
      border: '1px solid transparent',
      '&:focus': { outline: 'none' },
      '&.Mui-focusVisible': {
        outline: 'none',
        boxShadow: `0 0 0 2px ${theme.palette.grey[400]}`
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: 'none'
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        opacity: 1,
        borderColor: 'transparent',
        boxShadow: 'none'
      }
    }
  })
);

const SUCCESS_CLOSE_BG = '#205781';
const SUCCESS_CLOSE_HOVER = '#1a4968';

export const RevokeBureauConsentSuccessDialog = styled(MuiDialog)(
  ({ theme }: any) => ({
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.45)'
    },
    '& .MuiDialog-paper': {
      borderRadius: 14,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      maxWidth: 420,
      width: '100%',
      margin: theme.spacing(2)
    },

    '.rbc-success-content': {
      textAlign: 'center',
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(4),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },

    '.rbc-success-icon-wrap': {
      width: 64,
      height: 64,
      borderRadius: '50%',
      backgroundColor: '#e6f4ea',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto'
    },

    '.rbc-success-check': {
      color: '#1e7e34',
      fontSize: 36
    },

    '.rbc-success-title': {
      fontWeight: 700,
      fontSize: '1.375rem',
      lineHeight: 1.3,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2)
    },

    '.rbc-success-description': {
      color: theme.palette.text.secondary,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      marginBottom: theme.spacing(3),
      textAlign: 'center',
      maxWidth: 340,
      marginLeft: 'auto',
      marginRight: 'auto'
    },

    '.rbc-success-close-btn': {
      width: '100%',
      backgroundColor: SUCCESS_CLOSE_BG,
      color: '#fff',
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 8,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      boxShadow: 'none',
      outline: 'none',
      border: '1px solid transparent',
      '&:focus': { outline: 'none' },
      '&.Mui-focusVisible': {
        outline: 'none',
        boxShadow: `0 0 0 2px ${theme.palette.grey[400]}`
      },
      '&:hover': {
        backgroundColor: SUCCESS_CLOSE_HOVER,
        boxShadow: 'none'
      }
    }
  })
);
